import { curStateAccount } from "@/Redux/selector";
import {
  BackIcon,
  NotificationDotIcon,
  NotificationsIcon,
  RuleIcon,
  SettingsIcon,
} from "@/component/Icons";
import TurnsPill from "@/component/TurnsPill";
import Rule from "@/component/popups/ui-1/Rule";
import { useLoginModal } from '@/contexts/LoginModalContext';
import { IS_UPDATE_VERSION_3_PROJECT, IS_UPDATE_VERSION_3_PROJECT_LOGIN } from "@/helper/const";
import { hasAuthCookie } from "@/utils/auth";
import { useAppNavigate } from '@/utils/hooks/useAppNavigate';
import { getData } from "@/utils/localstorage";
import avatarDefault from "@assets/images/avatarDefault.svg";
import masterCrown from "@assets/web-v3-images//master-crown.png";
import btnImg from '@assets/web-v3-images/btn-buy.png';
import puzzleLogo from "@assets/web-v3-images/icons/puzzlex5.png";
import PATH from "@config/PATH";
import { Text } from '@radix-ui/themes';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import imgImage388 from "@assets/web-v3-images/b47741bad27ab0f27cde8e8002018ff420c68999.png";
import imgImage387 from "@assets/web-v3-images/c7fdad3699c9f7fbf9531760c98ea32429be3a18.png";
import svgPaths from "../../../page/components/Account/svg-joy4njkm5x";

/**
 * Header của trang chủ: avatar + tên người dùng, pill số lượt (turns),
 * và các nút hành động (settings, notifications).
 *
 * Nền peach gradient tạm đặt ở đây để header hiển thị đúng khi chưa dựng hero;
 * khi hero được dựng, background của trang sẽ phủ lên phần này.
 */
const HomeHeader = ({
  user = { name: "", avatarUrl: "", turns: 0 },
  hasNotification = true,
  isMaster = false,
  onAddTurns,
  onOpenSettings,
  onOpenNotifications,
  onOpenRules,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const navigateApp = useAppNavigate();
  const [openRule, setOpenRule] = useState(false);
  const { openLogin } = useLoginModal();
  const account = useSelector(curStateAccount);
  const isAuthed = hasAuthCookie() || Boolean(account?.userInfo) || Boolean(account?.token);

  // Nút back về app MyID: chỉ hiện khi trang được mở trong webview MyID (cờ
  // "login_with_app" do LayoutApp ghi vào localStorage sau khi phát hiện
  // msisdn_myid/token_myid do webview inject — xem web-v2 HeaderMobile).
  // Đọc lại mỗi khi `user` đổi vì cờ có thể được set SAU lần render đầu, ngay
  // lúc auto-login xong (cùng lúc account/userInfo cập nhật khiến Home re-render).
  const [showAppBack, setShowAppBack] = useState(() => Boolean(getData("login_with_app")));
  useEffect(() => {
    if (getData("login_with_app")) setShowAppBack(true);
  }, [user]);

  const handleBackToApp = (e) => {
    // Nút nằm trong div bấm-để-reload — chặn nổi bọt để không reload trang.
    e.stopPropagation();
    window.location.href = "mytel://back";
  };

  const initials = user?.name
    ?.split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Trang hồ sơ (feature profile-setting) đăng ký ở PATH.ACCOUNT = '/account'
  const handleToProfilePage = () => {
    if (!IS_UPDATE_VERSION_3_PROJECT_LOGIN) {
      navigate(PATH.ACCOUNT);
      return;
    }

    if (isAuthed) {
      navigateApp(PATH.ACCOUNT);
    } else {
      openLogin(() => navigateApp(PATH.ACCOUNT));
    }
  };

  const handleToLogin = () => {
    if (!IS_UPDATE_VERSION_3_PROJECT_LOGIN) {
      navigate(PATH.ACCOUNT);
      return;
    }

    if (isAuthed) {
      navigateApp(PATH.ACCOUNT);
    } else {
      openLogin(() => navigateApp(PATH.HOME));
    }
  };

  const handleToNotificationPage = () => {
    IS_UPDATE_VERSION_3_PROJECT ? navigateApp(PATH.NOTIFICATION) : navigate(PATH.NOTIFICATION);
  }

  const handleOpenRule = () => {
    setOpenRule(true);
  };

  return (
    <header className="bg-linear-to-b pt-6 md:gap-3 md:pt-9 max-[375px]:gap-1 z-2 min-h-[140px] md:min-h-[220px] max-[375px]:min-h-[100px]">
      {/* Left: nút back về app (chỉ trong webview MyID) + avatar + tên + pill số lượt */}
      <div className="flex items-center justify-between gap-2 md:gap-3 max-[375px]:gap-1">
        <div className="flex items-center gap-2 sm:mb-3 cursor-pointer" onClick={() => window.location.reload()}>
          {showAppBack && (
            <ActionButton
              label={t("home.header.backToApp")}
              onClick={handleBackToApp}
              className="shrink-0"
            >
              <BackIcon className="" />
            </ActionButton>
          )}
          <img alt='Avatar' src={puzzleLogo} className="w-8 sm:w-15 object-cover" />
          <span
            className={`font-bold ${isMaster ? "text-[rgba(255,255,255,1)]" : "text-[rgba(87,19,10,1)]"} text-[18px] md:text-[35px]`}
          >
            ShweDeal
          </span>
        </div>
        <div>
          {/* Right: nút hành động */}
          <div className="flex shrink-0 items-center justify-end gap-1 sm:gap-1.5 md:gap-2 w-[115px] sm:w-[205px]">
            <ActionButton label={t("home.header.rules")} onClick={handleOpenRule}>
              <RuleIcon className=" intro-tour-home__step-3" />
            </ActionButton>

            <ActionButton
              label={t("home.header.settings")}
              onClick={onOpenSettings ? onOpenSettings : handleToProfilePage}
            >
              <SettingsIcon className=" intro-tour-home__step-4" />
            </ActionButton>
            {
              IS_UPDATE_VERSION_3_PROJECT ? <ActionButton
                label={t("home.header.notifications")}
                onClick={onOpenNotifications ? onOpenNotifications : handleToNotificationPage}
              >
                <NotificationsIcon className=" intro-tour-home__step-5" />
                {(hasNotification && isAuthed) && (
                  <div className="absolute right-0.5 top-0.5 md:right-1 md:top-1">
                    <NotificationDotIcon className="size-3 md:size-4" />
                  </div>
                )}
              </ActionButton> : null
            }
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 md:gap-3 max-[375px]:gap-1">
        <div className={`flex min-w-0 items-center gap-2 ${isMaster && isAuthed ? 'mt-4' : ''}`}>
          <div className="relative shrink-0">
            {
              isAuthed ? <div className="btn-click-active grid size-18 place-items-center overflow-hidden rounded-full border-4 border-transparent [background:linear-gradient(to_bottom_right,#ffbe8a,#ff8a4c)_padding-box,linear-gradient(13deg,#F2996C,#FFE3CE_61%,#F2996C)_border-box] text-base font-bold text-white md:size-26 md:border-6 md:text-lg max-[375px]:size-13 max-[375px]:border-[3px]">
                <button
                  type="button"
                  onClick={handleToProfilePage}
                  aria-label={user?.name}
                  className="grid size-full place-items-center"
                >
                  {user?.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt=""
                      className="size-full object-cover"
                    />
                  ) : (
                    <img src={avatarDefault} alt="" className="size-full object-cover" />
                  )}
                </button>
              </div>
                :
                <button
                  onClick={handleToLogin}
                  className={`relative w-full max-w-[100%] outline-none btn-click-active mt-2`}
                >
                  <img
                    src={btnImg}
                    className="block w-20 sm:w-28 h-auto"
                    alt="Revive"
                  />
                  <Text
                    className={`absolute inset-0 flex items-center justify-center text-[10px] sm:text-[14px] font-extrabold text-white`}
                    style={{
                      WebkitTextStroke: "1px #FF692E",
                      paintOrder: "stroke fill",
                    }}
                  >
                    {t('header.login')}
                  </Text>
                </button>

            }
            {/* Vương miện master: đặt trên đỉnh avatar, tràn lên mép trên. */}
            {isMaster && isAuthed && (
              <img
                src={masterCrown}
                alt=""
                className="pointer-events-none absolute left-1/2 top-0 aspect-[87/69] w-[62%] -translate-x-1/2 -translate-y-[55%] object-contain drop-shadow-[0_2px_3px_rgba(180,70,10,0.4)]"
              />
            )}
          </div>
          <div className="flex min-w-0 w-full flex-col items-start gap-1 sm:gap-3">
            <h1 className="min-w-0 w-full max-w-full truncate text-base font-extrabold leading-none text-[#57130A] md:text-xl max-[375px]:text-sm">
              {user?.name?.length > 15 ? user?.name.slice(0, 15) + "..." : user?.name}
            </h1>
            {/* -------- */}
            {isAuthed && IS_UPDATE_VERSION_3_PROJECT &&
              <div
                onClick={() => navigate(PATH.RANK)}
                className="content-stretch flex gap-[6px] items-center pl-[3px] sm:pl-[6px] pr-[3px] sm:pr-[10px] py-[4px] relative rounded-[99px] shrink-0 cursor-pointer"
                style={{
                  backgroundImage: isMaster
                    ? "linear-gradient(256.354deg, rgb(255, 247, 207) 5.2335%, rgb(255, 211, 140) 44.081%, rgb(255, 242, 182) 92.051%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)"
                    : "linear-gradient(256.354deg, rgba(254,242,233,1) 5.2335%, rgba(246, 183, 149, 1) 44.081%, rgba(252, 220, 201, 1) 92.051%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)",
                }}
              >
                <div
                  aria-hidden
                  className={`absolute inset-0 pointer-events-none rounded-[99px] ${isMaster
                    ? 'border border-solid border-[#fff0aa]'
                    : `
                    p-[1px]
                    bg-[linear-gradient(90deg,#FFDAC4_0%,#F2A47A_50%,#F5B692_100%)]
                    [mask:linear-gradient(#fff_0_0)_padding-box,linear-gradient(#fff_0_0)]
                    [mask-composite:exclude]
                    [-webkit-mask-composite:xor]
                  `
                    }`}
                />
                <div className="relative shrink-0 size-[20px] sm:size-[34px]" data-name="image 387">
                  <div aria-hidden className="absolute inset-0 pointer-events-none">
                    {isMaster ? (
                      <div className="absolute inset-0 overflow-hidden">
                        <img
                          alt=""
                          className="absolute left-[-8.33%] max-w-none size-[116.67%] top-[-8.33%]"
                          src={imgImage388}
                        />
                      </div>
                    ) :
                      <img
                        alt=""
                        className="absolute max-w-none object-cover size-full"
                        src={imgImage387}
                      />
                    }
                  </div>
                </div>
                <p className="[word-break:break-word] font-['Inter:Semi_Bold',sans-serif] font-semibold leading-none not-italic relative shrink-0 text-[#713b12] text-[11px] sm:text-[17px] whitespace-nowrap">
                  {isMaster ? "Shwe Master" : "Explorer"}
                </p>
                <div
                  className="relative shrink-0 size-[15px] sm:size-[20px]"
                  data-name="solar:alt-arrow-right-linear"
                >
                  <div className="absolute inset-[20.83%_37.5%]" data-name="Vector">
                    <div className="absolute inset-[-9.18%_-21.43%]">
                      <svg
                        className="block size-full"
                        fill="none"
                        preserveAspectRatio="none"
                        viewBox="0 0 10 19.3334"
                      >
                        <path
                          d={svgPaths.p5ed6c80}
                          id="Vector"
                          stroke="var(--stroke-0, #A15C07)"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
        {/* Pill số lượt với nút "+" đặt tràn ở mép phải — dùng chung với trang Daily Reward, xem @/component/TurnsPill. */}
        <TurnsPill
          turns={user?.turns}
          onAddTurns={onAddTurns}
          className="h-9 pl-8 pr-9 md:h-12 md:border-[3px] md:py-1.5 md:pl-13 md:pr-11 max-[375px]:h-7 max-[375px]:pl-6 max-[375px]:pr-7"
          iconClassName="size-9 md:size-12 max-[375px]:size-7"
          textClassName="text-xs md:text-base max-[375px]:text-[11px]"
          addButtonClassName="size-6 right-0.5 md:right-1 md:size-8 max-[375px]:size-5"
        />
      </div>
      {/* Popup Rule: mở bằng nút RuleIcon (setOpenRule(true)), đóng qua nút thoát
          / overlay của PopupFrame -> onOpenChange(false). */}
      {openRule && <Rule open={openRule} onOpenChange={setOpenRule} />}
    </header>
  );
};

const ActionButton = ({ label, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className={`btn-click-active relative grid size-12 place-items-center md:size-16 ${className}`}
  >
    {children}
  </button>
);

export default HomeHeader;
